/* eslint-disable no-console */
import LocaleModule, { t } from 'Locale';
import {
    getUniqueId,
    getDeviceName,
    getMcoreDeviceType,
    getDeviceSystemVersion,
    getAppVersion,
    getNetInfo,
    Device,
    getDeviceAspectRatio
} from 'DeviceUtils';
import { isUserFreemium } from 'Utils/userUtils';
import defaultDeviceConfig from '../defaultDeviceConfig';
import STORAGE_KEYS from '../Storage/storageKeys';
import { startAppStateLogger, startNetworkStateLogger } from './helpers/appStateLogger';
import SystemInfo from './helpers/SystemInfoHelper';

class AppManager {
    constructor(storage, Api) {
        this.isLaunchLogged = false;
        /** @type {Storage} */
        this.storage = storage;
        /** @type {Api} */
        this.errorHandlers = [];
        this.api = new Api((error) => {
            this.errorHandlers.forEach((handler) => handler(error));
        });
        this.connector = this.api.backstageConnector;
    }

    /** @returns {Api} */
    getApi() {
        return this.api;
    }

    /** @returns {Storage} */
    getStorage() {
        return this.storage;
    }

    subscribeError = (handler) => {
        this.errorHandlers.push(handler);
    };

    unsubscribeError = (handler) => {
        const index = this.errorHandlers.findIndex(handler);
        if (index >= 0) {
            this.errorHandlers.splice(index);
        }
    };

    async initialize() {
        let userShouldLogin = false;
        let language = '';

        // eslint-disable-next-line no-useless-catch
        try {
            await this.initializeInDeviceConfig();

            const backstageConfig = await this.initializeBackstageConfig();

            this.api.setMCoreOrigin(backstageConfig);

            userShouldLogin = await this.initializeMCoreConnection();

            await this.setupApi();

            language = await this.getAppPreferredLanguage();

            const mCoreConfig = await this.initializeMCoreConfig(language);

            await this.initializeTranslations(language, mCoreConfig.stringModifications);

            await this.initializeLogger(mCoreConfig, userShouldLogin);

            await this.initializeMenus();

            if (!Device.isWeb) await this.getDataSaverValueFromStorage();
        } catch (error) {
            throw error;
        }

        return { userShouldLogin, language };
    }

    setupApi = () => this.api.setup(this.storage);

    initializeLogger = async (mCoreConfig, userShouldLogin) => {
        await this.initializeBehavioralLogger(mCoreConfig);
        const { blogs } = this.api.backstageConnector.events;
        blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'init' });
        if (userShouldLogin)
            blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'noAccountYet' });
    };

    initializeMenus = async () => {
        const menus = await this.api.fetchAllMenus(Device.isWeb ? 'MENU_WEB' : 'MENU_MOBILES');
        await this.storage.setItem(STORAGE_KEYS.BACKSTAGE_MENUS, { data: menus }, false, true);
    };

    async getLoggedUserData() {
        try {
            const { userInfo } = await this.getLoggedUserObjectDataFromStorage();

            if (userInfo) {
                return {
                    ...this.connector.session.accessTokenData,
                    ...userInfo
                };
            }

            return null;
        } catch (error) {
            console.log('Error: getLoggedUserData', error);
            throw error;
        }
    }

    /** If logged user object is not in device, try to retrieve it from memory store */
    async getLoggedUserObjectDataFromStorage() {
        const { data: storageData, valid } = await this.storage.getItem(
            STORAGE_KEYS.LOGGED_USER_OBJECT
        );

        if (valid && storageData) return storageData;

        const { data: inMemoryData, valid: inMemoryValid } = await this.storage.getItem(
            STORAGE_KEYS.LOGGED_USER_OBJECT,
            true
        );

        if (inMemoryValid && inMemoryData) return inMemoryData;

        return {};
    }

    async updateTokenInStorage(token, tokenData) {
        const currentUserObject = await this.getLoggedUserObjectDataFromStorage();

        const loggedUserObject = {
            ...currentUserObject,
            ...tokenData,
            userInfo: {
                ...currentUserObject.userInfo,
                ...token.userInfo
            }
        };

        await this.storage.setItem(
            STORAGE_KEYS.LOGGED_USER_OBJECT,
            { data: loggedUserObject },
            false
        );
    }

    async getDeviceConfigData() {
        try {
            const { data: deviceConfigData } = await this.storage.getItem(
                STORAGE_KEYS.DEVICE_CONFIG
            );
            return deviceConfigData;
        } catch (error) {
            console.log('Error: getDeviceConfigData', error);
            return defaultDeviceConfig;
        }
    }

    async getMCoreConfigData() {
        try {
            const { data: mCoreConfigData } = await this.storage.getItem(STORAGE_KEYS.MCORE_CONFIG);
            return mCoreConfigData;
        } catch (error) {
            console.log('Error: getMCoreConfigData', error);
            return null;
        }
    }

    async getBackstageConfigData() {
        try {
            const { data: backstageConfigData } = await this.storage.getItem(
                STORAGE_KEYS.BACKSTAGE_CONFIG
            );
            return backstageConfigData;
        } catch (error) {
            console.log('Error: getBackstageConfigData', error);
            return null;
        }
    }

    async setDeviceConfigData(data) {
        try {
            const { data: currentConfigData } = await this.storage.getItem(
                STORAGE_KEYS.DEVICE_CONFIG
            );

            await this.storage.setItem(STORAGE_KEYS.DEVICE_CONFIG, {
                data: {
                    ...currentConfigData,
                    ...data
                }
            });
        } catch (error) {
            console.log('Error: setDeviceConfigData', error);
        }
    }

    async login(username, password) {
        try {
            const { auth, session, events } = this.api.backstageConnector;
            const { rememberPassword } = await this.getDeviceConfigData();
            const { token: tokenData, consentId } = await auth.login({
                loginOrNickname: username,
                password
            });
            const { accessTokenData, deviceId } = this.connector.session;
            // If user is Freemium and we are on web, we will not allow login (more info in Jira ticket PRJ1260EXT-2696)
            if (isUserFreemium(accessTokenData) && Device.isWeb) {
                throw new Error(t('ERROR.LOGIN_NOT_VALID_FOR_PACKAGE___PACKAGE.BENEFIT'));
            }
            const { clientId } = session.accessTokenData;
            events.blogs.setConfig({ ...events.blogs.config, clientId });
            events.blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'login' });

            const loggedUserObject = {
                ...tokenData,
                deviceId,
                userInfo: {
                    ...tokenData.userInfo,
                    cookieUserId: consentId
                }
            };

            await this.storage.setItem(
                STORAGE_KEYS.LOGGED_USER_OBJECT,
                { data: loggedUserObject },
                false,
                !rememberPassword
            );

            return {
                ...accessTokenData,
                ...loggedUserObject.userInfo
            };
        } catch (error) {
            console.log('Error: login', error);
            throw error;
        }
    }

    async logout() {
        try {
            const { auth, events } = this.api.backstageConnector;
            await auth.logout();
            events.blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'logout' });
            await this.clearLoginData();
        } catch (error) {
            console.log('Error: logout', error);
            throw error;
        }
    }

    async clearLoginData() {
        try {
            await this.invalidateStorageItem(STORAGE_KEYS.LOGGED_USER_OBJECT);
        } catch (error) {
            console.log('Error: clearLoginData', error);
            throw error;
        }
    }

    async getPlaylistItems(pageItem) {
        let items = null;
        let total = 0;
        try {
            const playlist = await this.api.fetchPlaylist(pageItem);
            items = playlist.getItems();
            total = playlist.getTotalCount();
        } catch (err) {
            console.log('Error: getPlaylistItems', err);
        }

        return { items, total };
    }

    async fetchPageData(reference) {
        let pageData = null;
        try {
            pageData = await this.api.fetchPage(reference);
        } catch (err) {
            console.log('Error: fetchPageData', err);
        }

        return pageData;
    }

    async getMenus() {
        const { data } = await this.storage.getItem(STORAGE_KEYS.BACKSTAGE_MENUS, true);
        return data;
    }

    async getMySubscriptions() {
        const { data } = await this.storage.getItem(STORAGE_KEYS.MY_SUBSCRIPTIONS, true);
        return data;
    }

    async setMySubscriptions(mySubscriptions) {
        await this.storage.setItem(STORAGE_KEYS.MY_SUBSCRIPTIONS, {
            data: mySubscriptions
        });
    }

    /** TODO: refactor me. Make separated download service */
    // async getDownloads() {
    //     let downloads = [];
    //     try {
    //         downloads = await StreamStorageManager.getDownloads();
    //         window.sm = StreamStorageManager;
    //         return downloads.map((download) => {
    //             const { percentDownloaded, bytesDownloaded } = download.progress;
    //             const percent = download.state !== 3 ? percentDownloaded.toFixed(2) : 100;
    //             return {
    //                 id: download.request.id,
    //                 title: download.request.data.name,
    //                 subtitle: ` | ${formatBytes(bytesDownloaded)}`,
    //                 progressPercent: percent,
    //                 progressText: `${percent}%`,
    //                 paused: download.state !== 2 && download.state !== 3,
    //                 item: new ProgramModel(download.request.data.item.data)
    //             };
    //         });
    //     } catch (err) {
    //         console.log(err);
    //     }
    //     return downloads;
    // }

    async resetTranslations() {
        return this.invalidateStorageItem(STORAGE_KEYS.TRANSLATIONS);
    }

    async wipeCache() {
        this.storage.clearInMemoryStore();
        await this.api.wipeCache();
    }

    async refreshToken(loggedUserDataParam, params) {
        const loggedUserData =
            loggedUserDataParam || (await this.getLoggedUserObjectDataFromStorage());
        const { session } = this.api.backstageConnector;
        let refreshParams;
        if (params) {
            refreshParams = {
                refreshToken: loggedUserData.refreshToken,
                deviceName: params.deviceName,
                deviceType: params.deviceType,
                osVersion: params.osVersion,
                appVersion: params.appVersion
            };
        } else {
            refreshParams = {
                refreshToken: loggedUserData.refreshToken,
                deviceName: (await getDeviceName()) || 'iPhone 8',
                deviceType: getMcoreDeviceType() || 'OTT_IPHONE',
                osVersion: getDeviceSystemVersion(),
                appVersion: getAppVersion()
            };
        }
        try {
            const { token } = await session.refreshToken(refreshParams);
            return token.userInfo;
        } catch (error) {
            console.log('Error: refreshToken', error);
            if (error.errorCode !== '417' && error.errorCode !== 417) {
                await this.invalidateStorageItem(STORAGE_KEYS.LOGGED_USER_OBJECT);
            }
            throw error;
        }
    }

    async initializeMCoreConnection() {
        let userShouldLogin = false;
        let { language } = await this.getDeviceConfigData();
        // Fallback for versions where language was object
        if (language && typeof language !== 'string') {
            language = language.applicationLanguage;
        }
        const upperCaseLang = (language || defaultDeviceConfig.language).toUpperCase();
        const initParams = {
            dsid: await getUniqueId(),
            deviceName: (await getDeviceName()) || 'iPhone 8',
            deviceType: getMcoreDeviceType() || 'OTT_IPHONE',
            osVersion: getDeviceSystemVersion(),
            appVersion: getAppVersion(),
            language: upperCaseLang
        };
        const loggedUserData = await this.getLoggedUserObjectDataFromStorage();
        if (!this.userIsLoggedIn(loggedUserData)) {
            await this.api.init(initParams);
        } else {
            try {
                await this.refreshToken(loggedUserData, initParams);
            } catch (error) {
                if (error.errorCode === '417') throw error;
                console.log('Error: initializeMCoreConnection', error);
                if (
                    error.errorMessage.toLowerCase() === 'invalid refresh token' ||
                    error.errorMessage === 'INVALID_TOKEN_REFRESH' ||
                    error.errorCode === '403'
                ) {
                    await this.api.init(initParams);
                    userShouldLogin = true;
                }
            }
        }

        return userShouldLogin;
    }

    /** @private * */
    async reset() {
        try {
            this.storage.clearInMemoryStore();
            await this.invalidateStorageItem(STORAGE_KEYS.DEVICE_CONFIG);
            await this.invalidateStorageItem(STORAGE_KEYS.BACKSTAGE_CONFIG);
            await this.invalidateStorageItem(STORAGE_KEYS.MCORE_CONFIG);
            await this.resetTranslations();
            await this.initialize();
        } catch (error) {
            console.log('Error: reset', error);
            throw error;
        }
    }

    async initializeTranslations(language, stringModifications) {
        const shouldUpdateTranslations = true;
        // TODO: ověřit cache!!!!!!
        // await this.shouldUpdateTranslations(
        //     stringModifications,
        //     language
        // );
        // If there is a change, save those modifications into storage for later check
        if (shouldUpdateTranslations) {
            await this.storage.setItem(STORAGE_KEYS.STRING_MODIFICATIONS, {
                data: stringModifications
            });
        }
        const strings = await this.initializeTranslationStrings(language, shouldUpdateTranslations);
        if (strings) await this.changeLanguage(language, strings, LocaleModule);
        return strings;
    }

    async initializeTranslationStrings(language, forceTranslationsDownload = false) {
        const { valid: savedTranslationsAreValid, data: translationsData } =
            await this.storage.getItem(STORAGE_KEYS.TRANSLATIONS);
        const stringsLanguage = translationsData?.stringsLanguage;
        let strings = translationsData?.strings;
        const stringsDataExists = strings && stringsLanguage;

        if (
            !forceTranslationsDownload &&
            savedTranslationsAreValid &&
            stringsDataExists &&
            stringsLanguage === language
        ) {
            console.log('Strings restored from AsyncStorage');
        } else {
            strings = await this.api.fetchStrings(language);
            await this.storage.setItem(STORAGE_KEYS.TRANSLATIONS, {
                valid: true,
                data: { strings, stringsLanguage: language }
            });
            // Set default device language for next init - see initializeMCoreConnection method
            await this.setDeviceConfigData({ language });
            console.log('strings downloaded from API and saved to AsyncStorage');
        }

        return strings;
    }

    shouldUpdateTranslations = async (currentStringModifications, language) => {
        const { valid, data: savedStringModifications } = await this.storage.getItem(
            STORAGE_KEYS.STRING_MODIFICATIONS
        );
        if (!savedStringModifications || !valid) return true;

        const current = currentStringModifications.find(
            (modification) => modification.language.toLowerCase() === language.toLowerCase()
        );
        const saved = savedStringModifications.find(
            (modification) => modification.language.toLowerCase() === language.toLowerCase()
        );

        return !current || !saved || current.version !== saved.version;
    };

    changeLanguage = async (language, strings, localeModule) => {
        await localeModule.addResources(language, 'common', strings);
        await localeModule.changeLanguage(language);
        this.api.setConnectorLocale(language);
    };

    getAppPreferredLanguage = async () => {
        let language = '';

        // First download app language from mCore, if it fails, set default language from config
        try {
            const { applicationLanguage } = await this.api.fetchLanguage();
            // Application language comes in upper case
            return applicationLanguage.toLowerCase();
        } catch (error) {
            console.log('Error: getAppPreferredLanguage', error);
            const { language: deviceConfigLanguage } = await this.getDeviceConfigData();
            language = deviceConfigLanguage;
        }

        return language;
    };

    async initializeMCoreConfig(language) {
        const mCoreConfig = await this.api.fetchMCoreConfig(language);
        await this.storage.setItem(STORAGE_KEYS.MCORE_CONFIG, { data: mCoreConfig });
        console.log('mCore config downloaded and saved to DeviceStorage');

        return mCoreConfig;
    }

    async initializeBackstageConfig() {
        const backstageConfig = await this.api.fetchBackstageConfig();
        await this.storage.setItem(STORAGE_KEYS.BACKSTAGE_CONFIG, { data: backstageConfig });
        console.log('Backstage config downloaded and saved to DeviceStorage');

        return backstageConfig;
    }

    async initializeInDeviceConfig() {
        let configWasAlreadySetProperly = true;
        let item = await this.storage.getItem(STORAGE_KEYS.DEVICE_CONFIG);

        if (!this.isDeviceConfigComplete(defaultDeviceConfig, item.data)) {
            const data = {
                ...defaultDeviceConfig,
                ...item.data,
                aspectRatio: getDeviceAspectRatio()
            };
            item = await this.storage.setItem(STORAGE_KEYS.DEVICE_CONFIG, {
                data
            });
            configWasAlreadySetProperly = false;
            console.log('Device config was incomplete. Was now set with all values');
        }

        if (item.valid && item.data) {
            console.log('Device config is valid and set in DeviceStorage');
        } else {
            await this.storage.setItem(STORAGE_KEYS.DEVICE_CONFIG, { data: defaultDeviceConfig });
            console.log('Device config initialized from default and saved to DeviceStorage');
            configWasAlreadySetProperly = false;
        }

        return configWasAlreadySetProperly;
    }

    async invalidateStorageItem(key, merge = false) {
        if (!key) {
            throw new TypeError('`key` is not defined');
        }

        await this.storage.setItem(key, { valid: false, data: {} }, merge);
    }

    async initializeBehavioralLogger(config) {
        const {
            session,
            events: { blogs }
        } = this.api.backstageConnector;
        const { clientId, deviceId } = session.accessTokenData || {};
        const { ipAddress } = (await getNetInfo()) || {};
        const { behavioralLogsModuleUrl, pushBehavioralLogsEveryXSeconds } = config;
        blogs.setConfig({
            clientId: clientId?.toString() || '',
            appversion: getAppVersion(),
            deviceId: deviceId?.toString() || '',
            deviceType: getMcoreDeviceType(),
            ipaddress: ipAddress || ''
        });
        await SystemInfo.load();
        const systemInfo = SystemInfo.get();
        if (systemInfo) {
            blogs.logEvent({
                time: Date.now(),
                type: 'debug',
                subtype: 'deviceInfo',
                ...systemInfo
            });
        }
        blogs.startLogging(behavioralLogsModuleUrl, pushBehavioralLogsEveryXSeconds);
        if (!this.isLaunchLogged) {
            blogs.logEvent({ time: Date.now(), type: 'general', subtype: 'launch' });
            this.isLaunchLogged = true;
        }
        startAppStateLogger(blogs, behavioralLogsModuleUrl, pushBehavioralLogsEveryXSeconds);
        return startNetworkStateLogger(blogs);
    }

    userIsLoggedIn = (loggedUserData) =>
        loggedUserData && loggedUserData.accessToken && loggedUserData.userInfo;

    isDeviceConfigComplete = (deviceConfig, deviceConfigToCheck) => {
        if (!deviceConfigToCheck) return false;
        const keys = Object.keys(deviceConfig);
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) {
            if (deviceConfigToCheck[key] === undefined) return false;
        }
        if (deviceConfig?.aspectRatio !== getDeviceAspectRatio()) return false;
        return true;
    };

    /** Check for usage of data saving feature for streams */
    async getDataSaverValueFromStorage() {
        let dataSaver = null;
        try {
            dataSaver = await this.storage.getItem(STORAGE_KEYS.DATA_SAVER);
            if (dataSaver?.data?.enableSaver === null)
                await this.setDataSaverValueFromStorage(false);
        } catch (error) {
            console.log('Error: getDataSaverValueFromStorage throws: ', error);
        }
        return dataSaver;
    }

    /** Setting of data saving feature for streams */
    async setDataSaverValueFromStorage(dataSaverEnabled) {
        try {
            if (dataSaverEnabled !== undefined) {
                this.storage.setItem(STORAGE_KEYS.DATA_SAVER, {
                    data: { enableSaver: dataSaverEnabled }
                });
            } else {
                this.storage.setItem(STORAGE_KEYS.DATA_SAVER, {
                    data: { enableSaver: false }
                });
            }
        } catch (error) {
            console.log('Error: setDataSaverValueFromStorage throws: ', error);
        }
    }
}

export default AppManager;
